import React, { useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import TripleDots from '../../../assets/images/horizontal-dropdown.svg';
import CommonPopover from '../popover-without-border';

const DonutChartCard = ({
  title,
  isTotal = true,
  data,
  className,
  chartClassName,
  hidePercentage = false,
  range = false,
  options,
  open,
  option,
  setOption,
  setOpen,
  labelClassName,
  optionClassName,
  circleClassName,
  totalAgeEngagements = 0,
  centerText
}) => {
  // State to track selected items for filtering
  const [selectedItems, setSelectedItems] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Process data based on selected filters
  const processedData = React.useMemo(() => {
    // If no items are selected, show top 2 items and group the rest as "Others"
    if (selectedItems.length === 0) {
      // Sort data by value in descending order to find top items
      const sortedData = [...data].sort((a, b) => b.value - a.value);
      
      // Get top 2 items
      const topItems = sortedData.slice(0, 2);
      
      // Get remaining items
      const otherItems = sortedData.slice(2);
      
      // If there are other items, add them as "Others"
      if (otherItems.length > 0) {
        const othersValue = otherItems.reduce((sum, item) => sum + item.value, 0);
        const total = data.reduce((sum, item) => sum + item.value, 0);
        
        return [
          ...topItems,
          {
            label: "Others",
            value: othersValue,
            color: "#D3D3D3", // Gray color for "Others"
            percentage: ((othersValue / total) * 100).toFixed(2)
          }
        ];
      }
      
      return topItems;
    }
    
    // Filter selected items
    const selectedData = data.filter(item => 
      selectedItems.includes(item.label)
    );
    
    // Calculate total value for non-selected items
    const otherItems = data.filter(item => 
      !selectedItems.includes(item.label)
    );
    
    const othersValue = otherItems.reduce((sum, item) => sum + item.value, 0);
    
    // If there are non-selected items, add an "Others" category
    if (othersValue > 0) {
      // Calculate total for percentage calculation
      const total = data.reduce((sum, item) => sum + item.value, 0);
      
      // Add the "Others" category
      return [
        ...selectedData,
        {
          label: "Others",
          value: othersValue,
          color: "#D3D3D3", // Gray color for "Others"
          percentage: ((othersValue / total) * 100).toFixed(2)
        }
      ];
    }
    
    return selectedData;
  }, [data, selectedItems]);

  // Toggle item selection with a maximum of 2 selections
  const toggleItemSelection = (label) => {
    setSelectedItems(prev => {
      // If item is already selected, remove it
      if (prev.includes(label)) {
        return prev.filter(item => item !== label);
      } else {
        // If we already have 2 items selected and trying to add more, don't add
        if (prev.length >= 2) {
          // Show a console warning (you could replace this with a user-facing notification)
          console.warn('Maximum of 2 filters can be applied');
          return prev;
        }
        // Otherwise add the new item
        return [...prev, label];
      }
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSelectedItems([]);
    setIsFilterOpen(false);
  };

  const dataD = {
    labels: processedData.map((item) => item.label),
    datasets: [
      {
        label: 'Overall Responses',
        data: processedData.map((item) => item.value),
        backgroundColor: processedData.map((item) => item.color),
        hoverBackgroundColor: processedData.map((item) => item.color),
        borderWidth: 1,
        borderRadius: 10,
        borderJoinStyle: 'round'
      }
    ]
  };

  const optionsD = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      color: {
        enabled: true
      }
    },
    cutout: '90%',
    radius: '70%'
  };

  // Update totals based on what's displayed in the chart
  const displayedTotal = processedData.reduce((a, b) => +a + +b.value, 0);

  return (
    <div className={`md:p-4 p-4 w-full sm:mb-0 lg:w-1/5 min-w-full gap-2 sm:min-w-[202px] bg-[#FFFFFF] rounded-xl border border-[#E7E8EA] ${className} dark:bg-shoorah-darkBgTabColor dark:border-none flex flex-col justify-between`}>
      
      {/* title & dropdown */}   
      <div className="flex flex-row gap-2 px-1 justify-between items-start">
        <div className="w-full flex flex-row justify-between">
          <h2 className={`${labelClassName} lg:text-[20px] text-[18px] w-[99%] tracking-tight leading-[22px] flex items-start font-medium text-[#0B0F18] text-left pl-[0px] md:pl-[0px] dark:text-shoorah-darkBg`}>
            {title}
          </h2>
          <CommonPopover
            label={
              <>
                <img src={TripleDots} alt="dropdown" />
              </>
            }
            className=""
            contentClassName="w-48 mr-12 sm:mr-4 p-1 flex flex-col items-center"
          >
            <ul className="text-xs sm:text-base font-medium text-left w-full py-0 sm:py-2 space-y-1">
              <li className="px-3 font-semibold text-sm">Filter Items</li>
              {data.map((item, index) => (
                <li key={index} className="px-3 py-1">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.label)}
                      onChange={() => toggleItemSelection(item.label)}
                      className="form-checkbox"
                      disabled={!selectedItems.includes(item.label) && selectedItems.length >= 2}
                    />
                    <span className={!selectedItems.includes(item.label) && selectedItems.length >= 2 ? "text-gray-400" : ""}>
                      {item.label}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <ul className="text-xs sm:text-base font-medium text-left w-full py-0 sm:py-2 pt-1 space-y-1 border-t mt-1">
              <li className="px-3">
                <button 
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Clear Filters
                </button>
              </li>
            </ul>
          </CommonPopover>
        </div>
        {range && (
          <>
            <button onClick={() => setOpen(!open)} className="relative">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="dark:invert sm:w-[18px] sm:h-[16px]"
              >
                <path
                  d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                  stroke="#0B1D2E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                  stroke="#0B1D2E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                  stroke="#0B1D2E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {open && (
                <ul
                  className={`w-[120px] h-fit overflow-y-auto rounded-xl z-10 bg-white border border-shoorah-borderGray ms-auto absolute right-0 py-2 ${
                    optionClassName ? optionClassName : 'top-5'
                  }`}
                >
                  {options?.length > 0 &&
                    options?.map((item, index) => (
                      <li
                        className={`pl-4 pr-6 py-2 hover:bg-shoorah-borderGray cursor-pointer text-left capitalize ${
                          option === index ? 'bg-shoorah-borderGray' : ''
                        }`}
                        onClick={() => {
                          setOpen(false);
                          setOption(index);
                        }}
                      >
                        {item?.label}
                      </li>
                    ))}
                </ul>
              )}
            </button>
          </>
        )}
      </div>
      
      {/* donut circle */}
      <div className="relative flex max-h-[180px] justify-center items-center">
        {isTotal && (
          <div className="absolute text-center">
            {/* Always show the max value item from the original data, regardless of filtering */}
            {data.length > 0 && (
              <>
                <p className="text-sm text-[#606060] dark:text-shoorah-darkBg">
                  {data.reduce((max, item) => 
                    item.value > max.value ? item : max, data[0]
                  ).label}
                </p>
                <p className="text-2xl text-[#0B0F18] dark:text-shoorah-darkBg">
                  {totalAgeEngagements 
                    ? totalAgeEngagements 
                    : data.reduce((max, item) => 
                        item.value > max.value ? item : max, data[0]
                      ).value
                  }
                </p>
              </>
            )}
          </div>
        )}
        {displayedTotal === 0 && (
          <div
            className={`absolute text-center border-[4px] p-5 rounded-full ${
              circleClassName ? circleClassName : 'w-[120px] h-[120px]'
            }`}
            style={{ borderColor: processedData?.[processedData?.length - 1]?.color }}
          ></div>
        )}
        <Doughnut
          data={dataD}
          options={optionsD}
          style={{ width: '100%', height: '100%' }}
          className={chartClassName}
        />
      </div>
      
      {/* categories info */}
      <div className="flex flex-col md:flex-col lg:flex-col md:justify-between md:gap-5 lg:gap-3 gap-0">
        {processedData?.filter(item => item.label !== "Others").map((item, index) => (
          <div className="flex items-center justify-left" key={index}>
            <span
              className="w-3 h-3 rounded-full shrink-0 mr-2"
              style={{ backgroundColor: `${item.color}` }}
            ></span>
            <p className=" flex flex-row items-center gap-2 font-normal text-base text-[#606060] dark:text-shoorah-gray1 whitespace-nowrap">
              {item.label}:{' '}
              <span className="text-[#0B0F18] sm:text-xl font-medium text-[18px] dark:text-shoorah-darkBg leading-tight truncate w-full">
                {item.percentage === '100.00' ||
                item.percentage === '100.0' ||
                item.percentage === '100'
                  ? 100
                  : item.percentage}
                {!hidePercentage && '%'}
              </span>
            </p>
          </div>
        ))}
        {processedData?.filter(item => item.label !== "Others").length === 1 && <div className="lg:h-[25px] h-6"></div>}
      </div>
      
    </div>
  );
};

export default DonutChartCard;